import ReactPaginate from "react-paginate";
import {
  TFormValues,
  TTableColumn,
  TSortField,
  TSubSort,
} from "../../types/common";
import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableFoot,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { TableFilter } from "./TableFilter";
import styled from "styled-components";
import { beautify, getStatusColor, numberFormat } from "../../utils/helpers";
import { Select, Tag, Tooltip } from "antd";
import { EmptyWrapper } from "./Elements";
import Loader from "./Loader";
import { useState, useEffect } from "react";
import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";

type TTableProps = {
  loading: boolean;
  count: number;
  tableTitle?: string;
  tableFooter?: any;
  tableTotals?: Record<string, any>;
  tableColumns: TTableColumn[];
  tableData: any[];
  hideFilters?: boolean;
  // filters?: TFilter;
  // onFilterChange?(prop: TFilter): void;
  filters?: any;
  onFilterChange?: any;
  updateFormValues?(obj: TFormValues): void;
  tableStyle?: any;
  status?: any;
  noEmptyText?: boolean;
  showPagination?: boolean;
  tableHeaderItem?: any;
  pageItemsSize?: number;
  sort?: TSortField[] | TSortField;
  setSort?: (sort: TSortField[] | TSortField) => void;
};

export const Table = (props: TTableProps) => {
  const {
    tableData,
    tableColumns,
    tableFooter,
    tableTotals,
    tableTitle,
    loading,
    count,
    filters,
    hideFilters,
    tableStyle,
    onFilterChange,
    status,
    noEmptyText = true,
    showPagination = true,
    tableHeaderItem,
    pageItemsSize = 30,
    sort,
    setSort,
  } = props;

  const [pageSize, setPageSize] = useState(pageItemsSize);
  const [localTableData, setLocalTableData] = useState(tableData);
  const [isLocalSorted, setIsLocalSorted] = useState(false);

  const getSubSortLabel = (field: string, column: TTableColumn): string => {
    return column?.sort?.sub_sort_labels?.[field] || beautify(field);
  };

  const handleSubSortClick = (
    e: React.MouseEvent,
    column: TTableColumn,
    subSortField: string,
  ) => {
    e.stopPropagation(); // Prevent triggering the main sort
    if (!setSort || !sort) return;

    // Only works with single object sort (RegisterLayoutHandler)
    if (!Array.isArray(sort)) {
      const currentSubSorts = sort?.sub_sort || [];
      const existingSubSort = currentSubSorts.find(
        (s: TSubSort) => s.field === subSortField,
      );

      let newSubSorts: TSubSort[];

      if (existingSubSort) {
        // Toggle: asc -> desc -> remove
        if (existingSubSort.order === "asc") {
          newSubSorts = currentSubSorts.map((s: TSubSort) =>
            s.field === subSortField ? { ...s, order: "desc" as const } : s,
          );
        } else {
          // Remove this sub-sort
          newSubSorts = currentSubSorts.filter(
            (s: TSubSort) => s.field !== subSortField,
          );
        }
      } else {
        // Add new sub-sort with asc
        newSubSorts = [
          ...currentSubSorts,
          { field: subSortField, order: "asc" as const },
        ];
      }

      setSort({
        ...sort,
        sub_sort: newSubSorts.length > 0 ? newSubSorts : undefined,
      });
    }
  };

  const getSubSortState = (
    column: TTableColumn,
    subSortField: string,
  ): TSubSort | null => {
    if (!sort || Array.isArray(sort)) return null;

    const sortKey = column?.sort?.sort_key || column?.sort?.key || column?.id;
    if (sort.sort_key !== sortKey) return null;

    const currentSubSorts = sort.sub_sort || [];
    return (
      currentSubSorts.find((s: TSubSort) => s.field === subSortField) || null
    );
  };

  const getSortIcon = (column: TTableColumn) => {
    if (!column?.sort) return null;

    const sortKey = column?.sort?.sort_key || column?.sort?.key || column?.id;

    // Handle undefined sort
    if (!sort) {
      return (
        <div className="flexColumn alignCenter">
          <ArrowDropUp
            style={{
              color: "#bfbfbf",
              fontSize: 18,
              marginBottom: -11,
            }}
          />
          <ArrowDropDown style={{ color: "#bfbfbf", fontSize: 18 }} />
        </div>
      );
    }

    // Handle sort as single object (for RegisterLayoutHandler)
    if (!Array.isArray(sort)) {
      const isSorted = sort?.sort_key === sortKey;
      const color = "var(--selected-color)";

      if (isSorted) {
        return (
          <div className="flexColumn alignCenter">
            {sort?.sort_order === "asc" ? (
              <ArrowDropUp style={{ color, fontSize: 18 }} />
            ) : (
              <ArrowDropDown style={{ color, fontSize: 18 }} />
            )}
          </div>
        );
      } else {
        return (
          <div className="flexColumn alignCenter">
            <ArrowDropUp
              style={{
                color: "#bfbfbf",
                fontSize: 18,
                marginBottom: -11,
              }}
            />
            <ArrowDropDown style={{ color: "#bfbfbf", fontSize: 18 }} />
          </div>
        );
      }
    }

    // Handle sort as array (for other components)
    const sortArray = Array.isArray(sort) ? sort : [];
    const sortField = sortArray.find((s) => s?.sort_key === sortKey);
    const sortIndex = sortArray.findIndex((s) => s?.sort_key === sortKey);

    if (sortField) {
      const isPrimary = sortIndex === 0;
      const sortNumber =
        sortArray.length > 1 ? (sortIndex! + 1).toString() : "";
      const color = isPrimary ? "var(--selected-color)" : "#666";

      return (
        <div
          className="flexColumn alignCenter"
          style={{ position: "relative" }}
        >
          {sortField?.sort_order === "asc" ? (
            <ArrowDropUp style={{ color, fontSize: 18 }} />
          ) : (
            <ArrowDropDown style={{ color, fontSize: 18 }} />
          )}
          {sortNumber && (
            <span
              style={{
                position: "absolute",
                top: -8,
                right: -8,
                background: color,
                color: "white",
                borderRadius: "50%",
                width: 14,
                height: 14,
                fontSize: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
              }}
            >
              {sortNumber}
            </span>
          )}
        </div>
      );
    } else {
      return (
        <div className="flexColumn alignCenter">
          <ArrowDropUp
            style={{
              color: "#bfbfbf",
              fontSize: 18,
              marginBottom: -11,
            }}
          />
          <ArrowDropDown style={{ color: "#bfbfbf", fontSize: 18 }} />
        </div>
      );
    }
  };

  useEffect(() => {
    if (!isLocalSorted) {
      setLocalTableData(tableData);
    }
  }, [tableData, isLocalSorted]);

  const onSort = (column: TTableColumn) => {
    if (column?.sort && setSort) {
      const { localSort, firstClickSortOrder = "asc", sub_sort } = column?.sort;
      const key = column?.sort?.sort_key || column?.sort?.key || column?.id;

      // Handle sort as single object (for RegisterLayoutHandler)
      if (sort && !Array.isArray(sort)) {
        const currentSortKey = sort?.sort_key;
        const currentSortOrder = sort?.sort_order;
        const currentSubSort = sort?.sub_sort; // Preserve current sub-sort state

        let newSort: any = {};

        if (currentSortKey === key) {
          // Same column clicked - toggle order, preserve sub-sort state
          if (currentSortOrder === firstClickSortOrder) {
            // Second click: toggle to opposite order
            newSort = {
              sort_key: key,
              sort_order: firstClickSortOrder === "asc" ? "desc" : "asc",
              ...(currentSubSort && { sub_sort: currentSubSort }), // Preserve current state
            };
          } else {
            // Third click: clear sort
            newSort = { sort_key: "", sort_order: "asc" };
          }
        } else {
          // Different column clicked - initialize with column's default sub_sort if defined
          const initialSubSort = sub_sort
            ? sub_sort.map((field) => ({
                field,
                order: "asc" as const,
              }))
            : undefined;

          newSort = {
            sort_key: key,
            sort_order: firstClickSortOrder,
            ...(initialSubSort && { sub_sort: initialSubSort }),
          };
        }

        setSort(newSort);
        return;
      }

      const currentSortFields = (
        Array.isArray(sort) ? sort : []
      ) as TSortField[];
      const existingSortIndex = currentSortFields.findIndex(
        (s) => s.sort_key === key,
      );
      const isCurrentlySorted = existingSortIndex !== -1;

      let newSortFields: TSortField[] = [];

      if (localSort) {
        // For local sorting, we'll keep it simple and only support single column sorting
        let newSortKey = key;
        let newSortOrder = firstClickSortOrder;

        if (isCurrentlySorted) {
          const currentSort = currentSortFields[existingSortIndex];
          if (currentSort.sort_order === firstClickSortOrder) {
            // Second click: toggle to opposite order
            newSortOrder = firstClickSortOrder === "asc" ? "desc" : "asc";
          } else {
            // Third click: clear sort
            newSortKey = "";
            newSortOrder = "asc";
          }
        }

        if (newSortKey === "") {
          // Clear sort - reset to original data
          setIsLocalSorted(false);
          setLocalTableData(tableData);
          newSortFields = [];
        } else {
          setIsLocalSorted(true);
          const sortedData = [...localTableData].sort((a, b) => {
            // Get the original data values, not the formatted display values
            const aValue = (a as any)._original?.[key] ?? a[key];
            const bValue = (b as any)._original?.[key] ?? b[key];

            // Handle numeric sorting
            if (typeof aValue === "number" && typeof bValue === "number") {
              if (newSortOrder === "asc") {
                return aValue - bValue;
              } else {
                return bValue - aValue;
              }
            }

            // Handle string sorting
            const aStr = String(aValue || "").toLowerCase();
            const bStr = String(bValue || "").toLowerCase();

            if (newSortOrder === "asc") {
              return aStr.localeCompare(bStr);
            } else {
              return bStr.localeCompare(aStr);
            }
          });

          setLocalTableData(sortedData);
          newSortFields = [{ sort_key: newSortKey, sort_order: newSortOrder }];
        }
      } else {
        // Server sort - support multi-column sorting
        if (isCurrentlySorted) {
          // Clicking on already sorted column
          const currentSort = currentSortFields[existingSortIndex];
          if (currentSort.sort_order === firstClickSortOrder) {
            // Toggle to opposite order
            newSortFields = [...currentSortFields];
            newSortFields[existingSortIndex] = {
              ...currentSort,
              sort_order: firstClickSortOrder === "asc" ? "desc" : "asc",
            };
          } else {
            // Remove this sort field
            newSortFields = currentSortFields.filter((s) => s.sort_key !== key);
          }
        } else {
          // Clicking on unsorted column - add as primary sort
          const newSortField: TSortField = {
            sort_key: key,
            sort_order: firstClickSortOrder,
          };

          // If we already have 2 sorts, replace the secondary with the new primary
          if (currentSortFields.length >= 2) {
            newSortFields = [newSortField, currentSortFields[0]];
          } else {
            // Add as primary, shift existing to secondary
            newSortFields = [newSortField, ...currentSortFields];
          }
        }

        setIsLocalSorted(false);
      }

      setSort(newSortFields);
    }
  };

  const onChange = (key: string, value: string | number) => {
    const updatedFilters = { ...filters };
    delete updatedFilters.page;
    if (value === "" || value === null || value === undefined) {
      delete updatedFilters[key];
      if (onFilterChange) onFilterChange(updatedFilters);
      return;
    } else {
      if (onFilterChange) onFilterChange({ ...updatedFilters, [key]: value });
    }
  };

  function getCount() {
    // if (count > 1000) return `${(count / 1000).toFixed(0)}K`;
    return count;
  }

  // paginated table records
  const startValue =
    ((filters?.page || 1) - 1) * (filters?.page_size || pageSize) + 1;
  const endValue =
    ((filters?.page || 1) - 1) * (filters?.page_size || pageSize) +
    tableData?.length;
  const counter_text = (
    <>
      Showing of{" "}
      <b>
        {numberFormat(startValue)} - {numberFormat(endValue)}
      </b>{" "}
      of {getCount() || 0} items
    </>
  );
  const PerPageDropdown = () => {
    return (
      <div className="flexRow gap10 alignCenter">
        <span className="font13">Items per page</span>
        <Select
          options={[
            { label: "10", value: 10 },
            { label: "20", value: 20 },
            { label: "30", value: 30 },
            { label: "50", value: 50 },
            { label: "75", value: 75 },
            { label: "100", value: 100 },
          ]}
          onChange={(value) => {
            setPageSize(value);
            onFilterChange({ ...filters, page_size: value });
          }}
          defaultValue={pageSize}
        />
      </div>
    );
  };

  return (
    <TableWrapper className="">
      <TitleWrapper className="whiteBg flexCenter spaceCenter">
        <div className="flex gap10">
          <span className="capitalize bold">{tableTitle}</span>
          {status && (
            <Tag className="capitalize" color={getStatusColor(status)}>
              {beautify(status)}
            </Tag>
          )}
        </div>
        {/* COUNTER TEXT */}
        <FlexWrapper>
          {count > 0 && (
            <div className="flexRow gap10 alignCenter">
              {showPagination && <PerPageDropdown />}
              <span className="font13">{counter_text}</span>
            </div>
          )}
          {tableHeaderItem && <span className="font13">{tableHeaderItem}</span>}
        </FlexWrapper>
      </TitleWrapper>

      <div className="table-wrapper" style={{ ...tableStyle }}>
        <div className="responsive-table">
          <CTable style={{ ...tableStyle }} striped>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell style={{ fontSize: "85%" }} scope="col">
                  #
                </CTableHeaderCell>
                {tableColumns?.map((item, idx) => {
                  // Skip null/undefined items
                  if (!item) return null;

                  // Get the sort state for tooltip
                  const getSortTooltip = () => {
                    if (!item.sort) return "";
                    const sortKey =
                      item.sort?.sort_key || item.sort?.key || item.id;

                    if (!sort) return "Click to sort";

                    if (!Array.isArray(sort)) {
                      // Single object sort
                      if (sort.sort_key === sortKey) {
                        return sort.sort_order === "asc"
                          ? "Sorted: Ascending (click to descend)"
                          : "Sorted: Descending (click to clear)";
                      }
                      return "Click to sort";
                    }

                    // Array sort
                    const sortField = sort.find((s) => s.sort_key === sortKey);
                    if (sortField) {
                      return sortField.sort_order === "asc"
                        ? "Sorted: Ascending (click to descend)"
                        : "Sorted: Descending (click to remove)";
                    }
                    return "Click to sort";
                  };

                  return (
                    <CTableHeaderCell
                      key={item.id || idx}
                      className={item?.noWrap ? "no-wrap" : ""}
                      style={{
                        fontSize: "85%",
                        cursor: item?.sort ? "pointer" : "default",
                        userSelect: "none",
                        transition: "background-color 0.2s ease",
                      }}
                      scope="col"
                      onClick={() => onSort(item)}
                      onMouseEnter={(e) => {
                        if (item?.sort) {
                          (e.target as HTMLElement).style.backgroundColor =
                            "#f5f5f5";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (item?.sort) {
                          (e.target as HTMLElement).style.backgroundColor = "";
                        }
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        <Tooltip title={getSortTooltip()}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span className="capitalize">{item.header}</span>
                            {getSortIcon(item)}
                          </div>
                        </Tooltip>
                        {item?.sort?.sub_sort &&
                          item?.sort?.sub_sort?.length > 0 &&
                          sort &&
                          !Array.isArray(sort) &&
                          sort?.sort_key ===
                            (item?.sort?.sort_key ||
                              item?.sort?.key ||
                              item?.id) && (
                            <div
                              style={{
                                display: "flex",
                                gap: 4,
                                alignItems: "center",
                                justifyContent: "start",
                              }}
                            >
                              {item?.sort?.sub_sort?.map((subSortField) => {
                                const subSortState = getSubSortState(
                                  item,
                                  subSortField,
                                );
                                const isActive = subSortState !== null;
                                const label = getSubSortLabel(
                                  subSortField,
                                  item,
                                );

                                return (
                                  <Tooltip
                                    key={subSortField}
                                    title={`${label}: ${subSortState ? (subSortState.order === "asc" ? "Ascending" : "Descending") : "Click to sort"}`}
                                  >
                                    <SubSortBadge
                                      isActive={isActive}
                                      onClick={(e) =>
                                        handleSubSortClick(
                                          e,
                                          item,
                                          subSortField,
                                        )
                                      }
                                    >
                                      {label}
                                      {isActive &&
                                        (subSortState.order === "asc" ? (
                                          <ArrowDropUp
                                            style={{
                                              fontSize: 16,
                                              marginLeft: -2,
                                            }}
                                          />
                                        ) : (
                                          <ArrowDropDown
                                            style={{
                                              fontSize: 16,
                                              marginLeft: -2,
                                            }}
                                          />
                                        ))}
                                    </SubSortBadge>
                                  </Tooltip>
                                );
                              })}
                            </div>
                          )}
                      </div>
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>

              {hideFilters ? null : (
                <CTableRow>
                  <CTableHeaderCell
                    style={{ fontSize: "85%" }}
                    scope="col"
                  ></CTableHeaderCell>

                  {tableColumns?.map((row, i) => {
                    if (!row) return <CTableHeaderCell key={i} />;
                    return row.filter ? (
                      <CTableHeaderCell
                        key={i}
                        style={{
                          whiteSpace: "nowrap",
                          border: "1px solid #ddd",
                        }}
                      >
                        <TableFilter
                          name={row.filter?.key ? row.filter?.key : row.id}
                          onChange={onChange}
                          updateFormValues={onFilterChange}
                          updateFilterValues={onFilterChange}
                          filters={filters}
                          selectOptions={row.filter?.options}
                          {...row.filter}
                        />
                      </CTableHeaderCell>
                    ) : (
                      <CTableHeaderCell />
                    );
                  })}
                </CTableRow>
              )}
            </CTableHead>
            <CTableBody>
              {loading ? (
                <CTableRow>
                  <CTableHeaderCell
                    scope="row"
                    colSpan={tableColumns?.length + 1}
                    className="text-center"
                    style={{ whiteSpace: "nowrap", fontWeight: "normal" }}
                  >
                    <Loader />
                  </CTableHeaderCell>
                </CTableRow>
              ) : localTableData?.length ? (
                localTableData.map((item, idx) => (
                  <CTableRow>
                    <CTableHeaderCell style={{ fontSize: "85%" }} scope="row">
                      {item?.index || startValue + idx}
                    </CTableHeaderCell>
                    {tableColumns.map((column, colIdx) => {
                      if (!column) return <CTableDataCell key={colIdx} />;
                      return (
                        <CTableDataCell
                          key={column.id || colIdx}
                          style={{ fontSize: "85%" }}
                          className={column?.noWrap ? "no-wrap" : ""}
                        >
                          {item[column.id] ||
                            (noEmptyText && <EmptyWrapper>Empty</EmptyWrapper>)}
                        </CTableDataCell>
                      );
                    })}
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableHeaderCell
                    scope="row"
                    colSpan={tableColumns?.length + 1}
                    className="text-center font14"
                    style={{ whiteSpace: "nowrap", fontWeight: "normal" }}
                  >
                    No data available
                  </CTableHeaderCell>
                </CTableRow>
              )}
            </CTableBody>
            {tableTotals && Object.keys(tableTotals).length > 0 && (
              <CTableFoot>
                <CTableRow>
                  <CTableHeaderCell style={{ fontSize: "85%" }} scope="col">
                    Totals
                  </CTableHeaderCell>
                  {tableColumns?.map((column, colIdx) => {
                    if (!column) return <CTableHeaderCell key={colIdx} />;
                    return (
                      <CTableHeaderCell
                        key={column.id || colIdx}
                        className={column?.noWrap ? "no-wrap" : ""}
                        style={{ fontSize: "85%" }}
                        scope="col"
                      >
                        <span className="capitalize bold">
                          {tableTotals[column.id]}
                        </span>
                      </CTableHeaderCell>
                    );
                  })}
                </CTableRow>
              </CTableFoot>
            )}
            {tableFooter && (
              <CTableFoot>
                <CTableRow>
                  <CTableHeaderCell style={{ fontSize: "85%" }} scope="col">
                    #
                  </CTableHeaderCell>
                  {tableColumns?.map((column, colIdx) => {
                    if (!column) return <CTableHeaderCell key={colIdx} />;
                    return (
                      <CTableHeaderCell
                        key={column.id || colIdx}
                        className={column?.noWrap ? "no-wrap" : ""}
                        style={{ fontSize: "85%" }}
                        scope="col"
                      >
                        <span className="capitalize">
                          {tableFooter?.[column.id]}
                        </span>
                      </CTableHeaderCell>
                    );
                  })}
                </CTableRow>
              </CTableFoot>
            )}
          </CTable>
        </div>
      </div>

      <div className="kv-panel-after" />
      <div className="panel-footer">
        {showPagination && (
          <div className="kv-panel-pager">
            {loading ||
              (localTableData?.length > 0 && count ? (
                <ReactPaginate
                  breakLabel="..."
                  previousLabel={<span>«</span>}
                  onPageChange={({ selected }) =>
                    onChange("page", selected + 1)
                  }
                  pageRangeDisplayed={3}
                  pageCount={Math.ceil(count / pageSize)}
                  marginPagesDisplayed={3}
                  nextLabel={<span>»</span>}
                  renderOnZeroPageCount={null}
                  containerClassName="pagination justify-content-end"
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  activeClassName="active"
                  forcePage={(filters?.page || 1) - 1}
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                />
              ) : null)}
          </div>
        )}
        <div className="clearfix" />
      </div>
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #ddd;

  .panel-footer {
    padding: 0 20px;
  }
`;

const TitleWrapper = styled.div`
  padding: 10px 20px;
  border-bottom: 1px solid #ddd;
`;

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
`;

const SubSortBadge = styled.div<{ isActive: boolean }>`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 2px 6px;
  font-size: 9px;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  white-space: nowrap;

  background: transparent;
  color: ${({ isActive }) => (isActive ? "#333" : "#999")};
  border: 1px solid ${({ isActive }) => (isActive ? "#ccc" : "#e0e0e0")};

  &:hover {
    background: #f5f5f5;
    border-color: ${({ isActive }) => (isActive ? "#999" : "#ccc")};
    color: #333;
  }

  &:active {
    background: #ececec;
  }
`;
